import {
  ExpandLess,
  ExpandMore,
  MoreVert,
  Description,
  Person,
  Schedule,
  CalendarToday,
  ArrowBack,
  Download,
  ContentCopy,
  Warning,
  Search,
  AttachFile,
  FilterList,
} from "@mui/icons-material";
import React, { useState, useEffect, useMemo } from "react";
import "./appointmentDetailView.css"; // Import your CSS styles

const AppointmentDetailView = ({
  appointment,
  onBack,
  onAccept,
  onReject,
  appointments = [],
}) => {
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(
    appointment.id
  );

  const currentAppointment =
    appointments.find((apt) => apt.id === selectedAppointmentId) || appointment;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    const opts = { weekday: "short", month: "short", day: "numeric" };
    return d.toLocaleDateString("en-US", opts);
  };

  const formatTime = (timeString) => {
    return timeString || "";
  };

  return (
    <div className="adv-container">
      {/* Left Sidebar */}
      <aside className="adv-sidebar">
        <div className="adv-sidebar-header">
          <button className="adv-back-button" onClick={onBack}>
            <ArrowBack className="adv-back-icon" />
            <span className="adv-back-text">Back to Requests</span>
          </button>
        </div>

        <div className="adv-card-list">
          {appointments.map((apt) => {
            const isSelected = apt.id === selectedAppointmentId;
            return (
              <div
                key={apt.id}
                className={`adv-card ${isSelected ? "selected" : ""}`}
                onClick={() => setSelectedAppointmentId(apt.id)}
              >
                <div className="adv-card-content">
                  <div className="adv-card-avatar">
                    {apt.client?.first_name?.[0]}
                    {apt.client?.last_name?.[0]}
                  </div>
                  <div className="adv-card-text">
                    <div className="adv-card-name">
                      {apt.client?.first_name} {apt.client?.last_name}
                    </div>
                    <div className="adv-card-sub">
                      <span className="adv-card-type">
                        {apt.appointmentType?.appointment_name ||
                          "No appointment name"}
                      </span>
                      <span className="adv-card-datetime">
                        {formatDate(apt.requested_slot?.date)}{" "}
                        {formatTime(apt.requested_slot?.time)}
                      </span>
                    </div>
                    {/* Payment Status */}
                    <div className="adv-card-payment-status">
                      {apt.status === "authorization_hold" && (
                        <span style={{
                          backgroundColor: "#fff3cd",
                          color: "#856404",
                          padding: "2px 6px",
                          borderRadius: "3px",
                          fontSize: "10px",
                          fontWeight: "500"
                        }}>
                          Payment Hold
                        </span>
                      )}
                      {apt.status === "pending" && (
                        <span style={{
                          backgroundColor: "#f8d7da",
                          color: "#721c24",
                          padding: "2px 6px",
                          borderRadius: "3px",
                          fontSize: "10px",
                          fontWeight: "500"
                        }}>
                          No Payment
                        </span>
                      )}
                      {apt.status === "payment_captured" && (
                        <span style={{
                          backgroundColor: "#d4edda",
                          color: "#155724",
                          padding: "2px 6px",
                          borderRadius: "3px",
                          fontSize: "10px",
                          fontWeight: "500"
                        }}>
                          Payment Captured
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="adv-card-docs">
                    <Description className="adv-doc-icon" />
                    <span className="adv-doc-count">
                      {apt.documents?.length || 0}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      {/* Main Content */}
      <main className="adv-main">
        <div className="adv-main-content">
          {/* Header */}
          <div className="adv-header">
            <div className="adv-header-left">
              <div className="adv-title">
                {currentAppointment.appointmentType?.appointment_name ||
                  "No appointment name"}
              </div>
              <div className="adv-subheader">
                <div className="adv-subheader-left">
                  <div className="adv-sub-avatar">
                    {currentAppointment.client?.first_name?.[0]}
                    {currentAppointment.client?.last_name?.[0]}
                  </div>
                  <div className="adv-client-details">
                    <div className="adv-client-name">
                      {currentAppointment.client?.first_name}{" "}
                      {currentAppointment.client?.last_name}
                    </div>
                    <div className="adv-client-category">
                      {currentAppointment.appointmentType?.category ||
                        "Consultation"}
                    </div>
                  </div>
                </div>
                <div className="adv-subheader-right">
                  {formatDate(currentAppointment.requested_slot?.date)} at{" "}
                  {formatTime(currentAppointment.requested_slot?.time)}
                </div>
              </div>
              <div className="adv-amount">
                Total Amount: $
                {currentAppointment.appointmentType?.price || 120}
              </div>
            </div>
            <div className="adv-header-actions">
              <button
                className="adv-action-button primary"
                onClick={() => onAccept(currentAppointment.id)}
              >
                Accept
              </button>
              <button
                className="adv-action-button outlined"
                onClick={() => onReject(currentAppointment.id)}
              >
                Reject
              </button>
              <button className="adv-action-button outlined">Transfer</button>
            </div>
          </div>

          {/* Client Info Section */}
          <section className="adv-section">
            <h2 className="adv-section-title">Client Info</h2>
            <div className="adv-info-grid">
              <div className="adv-info-item">
                <Person className="adv-icon" />
                <span className="adv-info-label">Full legal name</span>
                <span className="adv-info-value">
                  {currentAppointment.client?.first_name}{" "}
                  {currentAppointment.client?.last_name}
                </span>
              </div>
              <div className="adv-info-item">
                <CalendarToday className="adv-icon" />
                <span className="adv-info-label">Date of birth</span>
                <span className="adv-info-value">
                  {currentAppointment.client?.dob || "Not provided"}
                </span>
              </div>
              <div className="adv-info-item">
                <Person className="adv-icon" />
                <span className="adv-info-label">Gender</span>
                <span className="adv-info-value">
                  {currentAppointment.client?.gender || "Not provided"}
                </span>
              </div>
              <div className="adv-info-item">
                <span className="adv-info-label adv-no-icon">Email</span>
                <span className="adv-info-value-inline">
                  {currentAppointment.client?.email || "Not provided"}
                  {currentAppointment.client?.email && (
                    <button className="adv-copy-button">
                      <ContentCopy className="adv-copy-icon" />
                    </button>
                  )}
                </span>
              </div>
              <div className="adv-info-item">
                <span className="adv-info-label adv-no-icon">Address</span>
                <span className="adv-info-value">
                  {currentAppointment.client?.address || "Not provided"}
                </span>
              </div>
              <div className="adv-info-item">
                <span className="adv-info-label adv-no-icon">Phone</span>
                <span className="adv-info-value">
                  {currentAppointment.client?.phone || "Not provided"}
                </span>
              </div>
              <div className="adv-info-item adv-message-item">
                <span className="adv-info-label adv-no-icon">Message</span>
                <div className="adv-message-box">
                  {currentAppointment.message || "No message provided"}
                </div>
              </div>
            </div>
          </section>

          {/* Documents Section */}
          <section className="adv-section">
            <h2 className="adv-section-title">Documents</h2>
            {currentAppointment.documents?.length > 0 ? (
              currentAppointment.documents.map((doc, idx) => (
                <div key={idx} className="adv-doc-item">
                  <div className="adv-doc-info">
                    <Description className="adv-doc-svg" />
                    <div className="adv-doc-texts">
                      <div className="adv-doc-name">
                        {doc.name || doc.filename || `Document_${idx + 1}.pdf`}
                      </div>
                      <div className="adv-doc-size">
                        {doc.size || "Unknown size"}
                      </div>
                    </div>
                  </div>
                  <button className="adv-download-button">
                    <Download className="adv-download-icon" />
                  </button>
                </div>
              ))
            ) : (
              <div className="adv-no-docs">No documents uploaded</div>
            )}
          </section>

          {/* Intake Form Section */}
          <section className="adv-section">
            <h2 className="adv-section-title">Intake Form</h2>
            <div className="adv-alert adv-alert-warning">
              <Warning className="adv-alert-icon" />
              <span>
                Accept the appointment to create and send an intake form.
              </span>
            </div>
            <button
              className="adv-section-button primary"
              onClick={() => onAccept(currentAppointment.id)}
            >
              Accept
            </button>
          </section>

          {/* AI Summary Section */}
          <section className="adv-section">
            <h2 className="adv-section-title">AI Summary</h2>
            <div className="adv-alert adv-alert-info">
              <Warning className="adv-alert-icon" />
              <div className="adv-alert-text">
                <div className="adv-alert-title">
                  AI suggests:{" "}
                  <strong>
                    {currentAppointment.ai_recommendation || "Accept"}
                  </strong>
                </div>
                <div className="adv-alert-body">
                  {currentAppointment.ai_reason ||
                    "No AI analysis available for this appointment."}
                </div>
              </div>
            </div>
            <button
              className="adv-section-button primary"
              onClick={() => onAccept(currentAppointment.id)}
            >
              Accept
            </button>
          </section>
        </div>
      </main>
    </div>
  );
};

export default AppointmentDetailView;
